import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Switch,
	Typography,
	Divider,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
	deletePromoFaq,
	getPromoFaqById,
	postPromoFaq,
	putPromoFaq,
} from "../../../../api/promo-faq";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyFaq() {
	return {
		locales: {
			es: { question: "", answer: "" },
			en: { question: "", answer: "" },
			ca: { question: "", answer: "" },
		},
		active: true,
	};
}

function LocaleFields({ label, localeKey, faq, onChange }) {
	const block = faq.locales?.[localeKey] || { question: "", answer: "" };
	return (
		<>
			<Typography variant="subtitle1" style={{ fontWeight: 700, marginTop: 8 }}>
				{label}
			</Typography>
			<TextField
				label="Pregunta"
				value={block.question}
				onChange={onChange(localeKey, "question")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				required={localeKey === "es"}
			/>
			<TextField
				label="Respuesta"
				value={block.answer}
				onChange={onChange(localeKey, "answer")}
				InputLabelProps={{ shrink: true }}
				margin="normal"
				variant="outlined"
				fullWidth
				multiline
				minRows={3}
				required={localeKey === "es"}
			/>
			<Divider style={{ marginTop: 16 }} />
		</>
	);
}

export default function EditPromoFaqPage() {
	const faqId = useParams().id;
	const history = useHistory();
	const [faq, setFaq] = useState(getEmptyFaq());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		let mounted = true;
		if (!faqId) {
			disableLoadingData();
			return () => {
				mounted = false;
			};
		}
		getPromoFaqById(faqId)
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) {
					const f = res.data || {};
					setFaq({
						locales: {
							es: {
								question: f.locales?.es?.question || "",
								answer: f.locales?.es?.answer || "",
							},
							en: {
								question: f.locales?.en?.question || "",
								answer: f.locales?.en?.answer || "",
							},
							ca: {
								question: f.locales?.ca?.question || "",
								answer: f.locales?.ca?.answer || "",
							},
						},
						active: f.active !== false,
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudo obtener la FAQ." });
				history.push("/promo-faqs");
			});
		return () => {
			mounted = false;
		};
	}, [faqId, disableLoadingData, history]);

	const handleLocaleChange = (localeKey, field) => (e) => {
		const value = e.target.value;
		setFaq((prev) => ({
			...prev,
			locales: {
				...prev.locales,
				[localeKey]: {
					...(prev.locales?.[localeKey] || {}),
					[field]: value,
				},
			},
		}));
	};

	function saveFaq() {
		const body = {
			locales: faq.locales,
			active: !!faq.active,
		};

		if (!String(body.locales?.es?.question || "").trim() || !String(body.locales?.es?.answer || "").trim()) {
			alertError({ customMessage: "La pregunta y respuesta en español son obligatorias." });
			return;
		}

		if (!faqId) {
			postPromoFaq(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({ title: "Guardado", customMessage: "FAQ creada correctamente." });
						history.push("/promo-faqs");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudo crear la FAQ." });
				});
			return;
		}

		putPromoFaq(faqId, body)
			.then((res) => {
				if (res.status === 200) {
					alertSuccess({ title: "Guardado", customMessage: "FAQ actualizada correctamente." });
					history.push("/promo-faqs");
				}
			})
			.catch((error) => {
				alertError({ error, customMessage: "No se pudo actualizar la FAQ." });
			});
	}

	if (isLoadingData) return <ContentSkeleton />;

	return (
		<MuiThemeProvider theme={theme}>
			<Card>
				<CardHeader title={faqId ? "Editar FAQ (web)" : "Nueva FAQ (web)"} />
				<CardBody>
					<Typography color="textSecondary" style={{ marginBottom: 8 }}>
						El orden se cambia desde el listado de FAQs. Los textos en español, inglés y catalán se muestran según el idioma de evenely.com.
					</Typography>

					<LocaleFields label="Español" localeKey="es" faq={faq} onChange={handleLocaleChange} />
					<LocaleFields label="English" localeKey="en" faq={faq} onChange={handleLocaleChange} />
					<LocaleFields label="Català" localeKey="ca" faq={faq} onChange={handleLocaleChange} />

					<FormControlLabel
						control={
							<Switch
								checked={!!faq.active}
								onChange={(e) => setFaq((prev) => ({ ...prev, active: e.target.checked }))}
								color="primary"
							/>
						}
						label="Activa (visible en la web)"
					/>
				</CardBody>
			</Card>

			<div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
				<Button variant="outlined" onClick={() => history.push("/promo-faqs")}>
					Volver
				</Button>
				<Button variant="contained" color="primary" onClick={saveFaq}>
					Guardar
				</Button>
				{faqId && (
					<Button variant="outlined" color="secondary" onClick={() => setOpenConfirmDialog(true)}>
						Eliminar
					</Button>
				)}
			</div>

			{faqId && (
				<ConfirmDialog
					title="¿Seguro que quieres eliminar esta FAQ?"
					open={openConfirmDialog}
					setOpen={setOpenConfirmDialog}
					onConfirm={() => {
						deletePromoFaq(faqId)
							.then((res) => {
								if (res.status === 204) {
									alertSuccess({ title: "Eliminada", customMessage: "FAQ eliminada correctamente." });
									history.push("/promo-faqs");
								}
							})
							.catch((error) => {
								alertError({ error, customMessage: "No se pudo eliminar la FAQ." });
							})
							.finally(() => setOpenConfirmDialog(false));
					}}
				/>
			)}
		</MuiThemeProvider>
	);
}
